import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Building2, Plus, CheckCircle2, Loader2, Pencil, Trash2, Globe, Target } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CompanyForm } from "../../components/CompanyForm";
import { BackToCampaigns } from "../../components/campaign/BackToCampaigns";
import { Button } from "../../components/ui/button";

export default function Companies() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null); // null=loading
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/companies").then((r) => setData(r.data)).catch((err) => {
      console.error("companies load error:", err);
      setData({ companies: [], active_company_id: null });
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshUser = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});

  const onSaved = () => { setShowForm(false); setEditing(null); load(); refreshUser(); };

  const select = async (company_id) => {
    setBusy(true);
    try {
      await api.post(`/companies/${company_id}/select`);
      setData((d) => ({ ...d, active_company_id: company_id }));
      await refreshUser();
      toast.success("Empresa seleccionada para tus campañas");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (company_id) => {
    if (!window.confirm("¿Eliminar esta empresa? Esta acción no se puede deshacer.")) return;
    setBusy(true);
    try {
      await api.delete(`/companies/${company_id}`);
      toast.success("Empresa eliminada");
      load(); refreshUser();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (data === null) {
    return (
      <div data-testid="companies-page" className="max-w-3xl">
        <BackToCampaigns />
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={32} /></div>
      </div>
    );
  }

  const companies = data.companies || [];
  const activeId = data.active_company_id;

  return (
    <div data-testid="companies-page" className="max-w-3xl">
      <BackToCampaigns />
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-3">
          <Building2 className="text-purple-500" size={26} />
          <h1 className="font-display font-black tracking-tight text-3xl">Empresas</h1>
        </div>
        {!showForm && (
          <Button data-testid="new-company" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-5 py-5">
            <Plus size={18} className="mr-2" /> Empresa nueva
          </Button>
        )}
      </div>
      <p className="text-zinc-400 mb-8">Da de alta tus empresas de multinivel y elige con cuál quieres hacer la campaña. La IA usará los datos de la empresa <strong className="text-yellow-impact">seleccionada</strong>.</p>

      {showForm && (
        <div className="mb-8 rounded-2xl border border-purple-500/30 bg-[#0e0a16] p-7">
          <h2 className="font-display font-bold text-xl mb-4">{editing ? "Editar empresa" : "Nueva empresa"}</h2>
          <CompanyForm initial={editing} onSaved={onSaved} onCancel={() => { setShowForm(false); setEditing(null); }} />
        </div>
      )}

      {companies.length === 0 && !showForm ? (
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-10 text-center">
          <Building2 className="mx-auto text-zinc-600 mb-3" size={32} />
          <p className="text-zinc-400 mb-4">Aún no tienes empresas guardadas.</p>
          <Button data-testid="new-company-empty" onClick={() => setShowForm(true)} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-5">
            <Plus size={18} className="mr-2" /> Agregar mi primera empresa
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {companies.map((c) => {
            const isActive = c.company_id === activeId;
            return (
              <div key={c.company_id} data-testid="company-card" className={`rounded-2xl border p-5 transition-all ${isActive ? "border-purple-500 bg-purple-500/10" : "border-white/10 bg-[#121212]"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-bold text-lg">{c.name}</h3>
                      {isActive && <span className="inline-flex items-center gap-1 text-[11px] bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full font-semibold"><CheckCircle2 size={12} /> Activa</span>}
                    </div>
                    {c.products_services && <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{c.products_services}</p>}
                    {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-purple-400 inline-flex items-center gap-1 mt-1"><Globe size={12} /> {c.source_url}</a>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid="edit-company" onClick={() => { setEditing(c); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-zinc-400 hover:text-white p-2" title="Editar"><Pencil size={16} /></button>
                    <button data-testid="delete-company" onClick={() => remove(c.company_id)} disabled={busy} className="text-zinc-400 hover:text-red-400 p-2" title="Eliminar"><Trash2 size={16} /></button>
                  </div>
                </div>
                {!isActive && (
                  <Button data-testid="select-company" onClick={() => select(c.company_id)} disabled={busy} className="mt-4 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-xl">
                    <Target size={16} className="mr-2" /> Seleccionar para campaña
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
